import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Filter, Grid, List, RefreshCw, ChevronDown, ChevronRight, Palette, X, Layers, Building2, CheckSquare, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { ColorImage } from './fabric/ColorImage';
import { ColorCard } from './fabric/ColorCard';
import { ColorDetailModal, fabricTypeBadge } from './fabric/ColorDetailModal';
import { RegenerateJobBanner } from './fabric/RegenerateJobBanner';
import { BlackoutRendersPanel } from './fabric/BlackoutRendersPanel';
import { BlackoutQcPanel } from './fabric/BlackoutQcPanel';
import { RoomTypeSelect } from './fabric/RoomTypeSelect';
import { BudgetConfirmDialog } from './fabric/BudgetConfirmDialog';
import { DEFAULT_ROOM_TYPE } from '../../lib/roomTypes';
import { useVisualizationJob } from '../../hooks/useVisualizationJob';
import { readGalleryCache, writeGalleryCache } from '../../lib/imageCache';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const EMPTY_FILTERS = { brands: [], collections: [], fabric_types: [] };

const brandColors = {
  'DW': 'from-blue-500 to-blue-700',
  'Coulisse': 'from-purple-500 to-purple-700',
  'Unknown': 'from-gray-500 to-gray-700'
};

export const FabricColorsTab = ({ token }) => {
  const [colors, setColors] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [selectedFabricType, setSelectedFabricType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [viewMode, setViewMode] = useState('grid');
  const [selectedColor, setSelectedColor] = useState(null);
  const [expandedBrands, setExpandedBrands] = useState({});

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRoomType, setBulkRoomType] = useState(DEFAULT_ROOM_TYPE);
  const [budget, setBudget] = useState(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedBrand !== 'all') params.append('brand', selectedBrand);
    if (selectedCollection !== 'all') params.append('collection', selectedCollection);
    if (selectedFabricType !== 'all') params.append('fabric_type', selectedFabricType);
    if (searchQuery) params.append('search', searchQuery);
    return params.toString();
  }, [selectedBrand, selectedCollection, selectedFabricType, searchQuery]);

  const applyData = useCallback((data) => {
    setColors(data.colors || []);
    setGrouped(data.grouped || {});
    setFilters(data.filters || EMPTY_FILTERS);
    setExpandedBrands(Object.fromEntries(Object.keys(data.grouped || {}).map((b) => [b, true])));
  }, []);

  const fetchColors = useCallback(async ({ silent = false } = {}) => {
    const cached = readGalleryCache(queryString);
    if (cached) {
      applyData(cached);
      setLoading(false);
    } else if (!silent) {
      setLoading(true);
    }
    try {
      const { data } = await axios.get(`${API_URL}/admin/fabrics/colors/gallery?${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      applyData(data);
      writeGalleryCache(queryString, data);
    } catch (error) {
      console.error('Failed to fetch colors:', error);
      if (!cached) toast.error('Failed to load fabric colors');
    } finally {
      setLoading(false);
    }
  }, [queryString, token, applyData]);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  const handleJobComplete = useCallback((job) => {
    fetchColors({ silent: true });
    setSelectedIds(new Set());
    setSelectMode(false);
    setSelectedColor((current) => {
      if (!current) return current;
      const mine = job.results.filter((r) => r.color_id === current.id && r.status === 'success');
      if (!mine.length) return current;
      const last = mine[mine.length - 1];
      const scenes = { ...(current.room_visualizations || {}) };
      mine.forEach((r) => { if (r.room_type) scenes[r.room_type] = r.url; });
      return { ...current, room_visualizations: scenes, room_visualization_url: last.url, ai_visualization_url: last.url, visualization_room_type: last.room_type || current.visualization_room_type };
    });
  }, [fetchColors]);

  const { job, startJob, adoptJob, isRunning, dismissJob } = useVisualizationJob(token, handleJobComplete);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/fabrics/colors/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(data.message);
      fetchColors({ silent: true });
    } catch (error) {
      toast.error('Failed to sync colors');
    } finally {
      setSyncing(false);
    }
  };

  const handleColorUpdated = (updated) => {
    setSelectedColor(updated);
    setColors((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
    fetchColors({ silent: true });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const openBudget = async ({ title, description, estimateRequest, run }) => {
    setBudget({ title, description, estimate: null, loading: true, run });
    try {
      const estimate = await estimateRequest();
      setBudget((b) => b && { ...b, estimate, loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not estimate cost');
      setBudget(null);
    }
  };

  const confirmBudget = async () => {
    if (!budget?.run) return;
    setBudget((b) => ({ ...b, confirming: true }));
    try {
      await budget.run();
      setBudget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start generation');
      setBudget((b) => b && { ...b, confirming: false });
    }
  };

  const handleBulkRegenerate = () => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    openBudget({
      title: bulkRoomType === 'all' ? 'Generate all missing room scenes' : 'Regenerate selected visualizations',
      description: `${ids.length} color${ids.length === 1 ? '' : 's'} selected.`,
      estimateRequest: async () => (await axios.post(`${API_URL}/admin/fabrics/colors/estimate`, { color_ids: ids, room_type: bulkRoomType }, { headers: { Authorization: `Bearer ${token}` } })).data,
      run: () => startJob(ids, bulkRoomType),
    });
  };

  const toggleBrandExpanded = (brand) => setExpandedBrands((prev) => ({ ...prev, [brand]: !prev[brand] }));

  const filteredCollections = useMemo(() => {
    if (selectedBrand === 'all') return filters.collections;
    return [...new Set(colors.filter((c) => c.brand_name === selectedBrand).map((c) => c.collection_name))];
  }, [selectedBrand, colors, filters.collections]);

  const clearFilters = () => {
    setSelectedBrand('all');
    setSelectedCollection('all');
    setSelectedFabricType('all');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedBrand !== 'all' || selectedCollection !== 'all' || selectedFabricType !== 'all' || searchQuery;

  return (
    <div className="space-y-6" data-testid="fabric-colors-tab">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800">Fabric Color Gallery</h2>
          <p className="text-neutral-500 mt-1" data-testid="gallery-summary">
            {colors.length} colors across {filters.brands.length} brands and {filters.collections.length} collections
          </p>
        </div>

        <div className="flex items-center gap-2">
          {viewMode === 'grid' && !selectMode && (
            <Button variant="outline" onClick={() => setSelectMode(true)} className="gap-2" data-testid="select-mode-btn">
              <CheckSquare className="w-4 h-4" /> Select
            </Button>
          )}
          <Button variant="outline" onClick={handleSync} disabled={syncing} className="gap-2" data-testid="sync-colors-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Collections
          </Button>
        </div>
      </div>

      <RegenerateJobBanner job={job} onDismiss={dismissJob} />
      <BlackoutRendersPanel token={token} isRunning={isRunning} onJobStarted={adoptJob} refreshKey={job?.status} />
      <BlackoutQcPanel token={token} refreshKey={job?.status} />

      {selectMode && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-neutral-50 px-4 py-3"
          data-testid="bulk-select-toolbar"
        >
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium" data-testid="selected-count">{selectedIds.size} selected</span>
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setSelectedIds(new Set(colors.map((c) => c.id)))} data-testid="select-all-btn">
              Select all visible
            </Button>
            <Button variant="link" size="sm" className="h-auto p-0 text-neutral-500" onClick={() => setSelectedIds(new Set())} data-testid="deselect-all-btn">
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <RoomTypeSelect value={bulkRoomType} onChange={setBulkRoomType} testId="bulk-room-type-select" className="w-52" allowAll />
            <Button onClick={handleBulkRegenerate} disabled={selectedIds.size === 0 || isRunning} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="bulk-regenerate-btn">
              <Sparkles className="w-4 h-4" /> Regenerate Selected ({selectedIds.size})
            </Button>
            <Button variant="ghost" onClick={exitSelectMode} data-testid="exit-select-mode-btn">Done</Button>
          </div>
        </div>
      )}

      <Tabs value={viewMode} onValueChange={(v) => { setViewMode(v); if (v !== 'grid') exitSelectMode(); }} className="w-full">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b">
          <TabsList className="bg-neutral-100">
            <TabsTrigger value="grid" className="gap-2" data-testid="view-grid-tab"><Grid className="w-4 h-4" /> Grid</TabsTrigger>
            <TabsTrigger value="grouped" className="gap-2" data-testid="view-grouped-tab"><Layers className="w-4 h-4" /> By Brand</TabsTrigger>
            <TabsTrigger value="list" className="gap-2" data-testid="view-list-tab"><List className="w-4 h-4" /> List</TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input placeholder="Search colors..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 w-48" data-testid="color-search-input" />
            </div>

            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger className="w-36" data-testid="brand-filter"><Building2 className="w-4 h-4 mr-2 text-neutral-400" /><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {filters.brands.map((brand) => <SelectItem key={brand} value={brand}>{brand}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={selectedCollection} onValueChange={setSelectedCollection}>
              <SelectTrigger className="w-44" data-testid="collection-filter"><Palette className="w-4 h-4 mr-2 text-neutral-400" /><SelectValue placeholder="Collection" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Collections</SelectItem>
                {filteredCollections.map((coll) => <SelectItem key={coll} value={coll}>{coll}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={selectedFabricType} onValueChange={setSelectedFabricType}>
              <SelectTrigger className="w-40" data-testid="fabric-type-filter"><Filter className="w-4 h-4 mr-2 text-neutral-400" /><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {filters.fabric_types.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-neutral-500" data-testid="clear-filters-btn">
                <X className="w-4 h-4" /> Clear
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="gallery-loading">
            {[...Array(12)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse">
                <div className="aspect-square bg-neutral-200 rounded-lg mb-2" />
                <div className="h-4 bg-neutral-200 rounded w-3/4 mb-1" />
                <div className="h-3 bg-neutral-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <TabsContent value="grid" className="mt-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="color-grid">
                {colors.map((color) => (
                  <ColorCard
                    key={color.id || color.source_color_id}
                    color={color}
                    selectMode={selectMode}
                    selected={selectedIds.has(color.id)}
                    onSelect={toggleSelected}
                    onOpen={setSelectedColor}
                  />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="grouped" className="mt-0 space-y-6">
              {Object.entries(grouped).map(([brandName, collections]) => (
                <div key={brandName} className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleBrandExpanded(brandName)}
                    className={`w-full flex items-center justify-between p-4 bg-gradient-to-r ${brandColors[brandName] || brandColors['Unknown']} text-white`}
                    data-testid={`brand-group-${brandName}`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5" />
                      <span className="font-semibold text-lg">{brandName}</span>
                      <Badge className="bg-white/20 text-white border-0">
                        {Object.values(collections).reduce((sum, c) => sum + c.colors.length, 0)} colors
                      </Badge>
                    </div>
                    {expandedBrands[brandName] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>

                  {expandedBrands[brandName] && (
                    <div className="divide-y">
                      {Object.entries(collections).map(([collectionName, data]) => (
                        <div key={collectionName} className="p-4 bg-white">
                          <div className="flex items-center gap-3 mb-4">
                            <Palette className="w-4 h-4 text-neutral-400" />
                            <h3 className="font-medium">{collectionName}</h3>
                            <Badge className={`text-xs ${fabricTypeBadge(data.fabric_type)}`}>{data.fabric_type}</Badge>
                            <span className="text-sm text-neutral-400">{data.colors.length} colors</span>
                          </div>
                          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                            {data.colors.map((color) => (
                              <div key={color.id || color.source_color_id} className="group cursor-pointer" onClick={() => setSelectedColor(color)}>
                                <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent group-hover:border-amber-400 transition-colors">
                                  <ColorImage src={color.room_visualization_url || color.ai_visualization_url || color.image_url} alt={color.name} hex={color.hex_color} className="w-full h-full" />
                                </div>
                                <p className="text-xs text-center mt-1 truncate" title={color.name}>{color.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="list" className="mt-0">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full" data-testid="color-list-table">
                  <thead className="bg-neutral-50">
                    <tr>
                      {['Preview', 'Color Name', 'Brand', 'Collection', 'Type', 'Hex'].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-sm font-medium text-neutral-600">{h}</th>
                      ))}
                      <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600">AI Viz</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {colors.map((color) => (
                      <tr key={color.id || color.source_color_id} className="hover:bg-neutral-50 cursor-pointer" onClick={() => setSelectedColor(color)}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded border" style={{ backgroundColor: color.hex_color || '#ccc' }} />
                            <div className="w-12 h-12 rounded overflow-hidden">
                              <ColorImage src={color.room_visualization_url || color.image_url} alt={color.name} hex={color.hex_color} className="w-full h-full" />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium">{color.name}</td>
                        <td className="px-4 py-3"><Badge variant="secondary">{color.brand_name}</Badge></td>
                        <td className="px-4 py-3 text-neutral-600">{color.collection_name}</td>
                        <td className="px-4 py-3"><Badge className={`text-xs ${fabricTypeBadge(color.fabric_type)}`}>{color.fabric_type}</Badge></td>
                        <td className="px-4 py-3"><code className="text-xs bg-neutral-100 px-2 py-1 rounded">{color.hex_color || '-'}</code></td>
                        <td className="px-4 py-3 text-center">
                          {color.room_visualization_url || color.ai_visualization_url
                            ? <Badge className="bg-green-100 text-green-700">Yes</Badge>
                            : <Badge variant="secondary">No</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>

      {selectedColor && (
        <ColorDetailModal
          color={selectedColor}
          token={token}
          onClose={() => setSelectedColor(null)}
          onRegenerate={(id, roomType) => startJob(id, roomType)}
          onGenerateMissing={(id, rooms) => openBudget({
            title: 'Generate missing room scenes',
            description: `${selectedColor?.name}: ${rooms.length} scene${rooms.length === 1 ? '' : 's'} missing.`,
            estimateRequest: async () => (await axios.get(`${API_URL}/admin/fabrics/colors/${id}/scenes/missing`, { headers: { Authorization: `Bearer ${token}` } })).data.estimate,
            run: async () => {
              const { data } = await axios.post(`${API_URL}/admin/fabrics/colors/${id}/scenes/generate-missing`, { rooms }, { headers: { Authorization: `Bearer ${token}` } });
              adoptJob(data);
              toast.info(`Generating ${data.total} room scenes in the background`);
            },
          })}
          regenerating={isRunning}
          onColorUpdated={handleColorUpdated}
        />
      )}

      <BudgetConfirmDialog
        open={!!budget}
        onOpenChange={(open) => !open && setBudget(null)}
        title={budget?.title}
        description={budget?.description}
        estimate={budget?.estimate}
        loading={budget?.loading}
        confirming={budget?.confirming}
        onConfirm={confirmBudget}
      />

      {!loading && colors.length === 0 && (
        <div className="text-center py-16" data-testid="gallery-empty-state">
          <Palette className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-600">No colors found</h3>
          <p className="text-neutral-500 mt-1">
            {hasActiveFilters ? 'Try adjusting your filters or search query' : 'Click "Sync from Collections" to import colors'}
          </p>
          {hasActiveFilters && <Button variant="outline" onClick={clearFilters} className="mt-4">Clear Filters</Button>}
        </div>
      )}
    </div>
  );
};

export default FabricColorsTab;
